import React from 'react';

import SettingsShell from 'screens/Settings/components/wrappers/SettingsShell';
import FormHeader from 'screens/Settings/components/atoms/FormHeader';
import { useTranslation } from 'react-i18next';
import { Divider, Segment, Button, Progress, Icon } from 'semantic';
import { Stack, Group } from 'components/Layout/helpers';
import { getTier } from 'utils/installer-partner-program';
import { useUser } from 'contexts/user';
import { formatDate } from 'utils/date';

const links = {
  nl: 'https://help.e-flux.io/nl/articles/10749012-partnerprogramma-voor-installateurs-bij-e-flux-by-road',
  default:
    'https://help.e-flux.io/en/articles/10749012-partner-program-for-installers-at-e-flux-by-road',
};

export default function InstallerPartnerProgram() {
  const { t, i18n } = useTranslation();
  const { user } = useUser();

  if (!user) {
    return null;
  }

  const account = user.account;
  if (!account) {
    return null;
  }

  if (!account.installerPartnerProgram?.currentTier) {
    return null;
  }

  const tier = getTier(account.installerPartnerProgram?.currentTier, t);
  const nextTier = tier.nextTier ? getTier(tier.nextTier, t) : null;

  const currentConnectorsCount =
    account.installerPartnerProgram?.currentConnectorsCount;

  const remainingConnectorsToNextTier =
    nextTier?.min &&
    currentConnectorsCount &&
    nextTier?.min - currentConnectorsCount;

  const progress =
    nextTier?.min &&
    currentConnectorsCount &&
    (currentConnectorsCount / nextTier?.min) * 100;

  const link = (links as any)[i18n.language] || links.default;

  return (
    <SettingsShell>
      <>
        <FormHeader
          size="large"
          pb={5}
          pt={5}
          text={t(
            'settings.organization.installerPartnerProgram.title',
            'Installer Partner Program'
          )}
        />
        <Divider />
        <p style={{ maxWidth: '600px' }}>
          {t(
            'settings.organization.installerPartnerProgram.description',
            'As an installer, you can earn an extra income based on the number of connectors you install and activate per year.'
          )}
        </p>
        <FormHeader
          size="medium"
          pb={5}
          pt={5}
          text={t(
            'settings.organization.installerPartnerProgram.currentTier',
            'Current Tier'
          )}
        />
        <Segment style={{ padding: '24px' }}>
          <Stack spacing={16}>
            <Group align="center">
              <Icon name={tier.iconName} size="big" />
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '24px',
                }}>
                {tier.label}
              </div>
            </Group>
            <Stack>
              <p>
                {t(
                  'settings.organization.installerPartnerProgram.currentActiveConnectors',
                  'Current no. of active connectors:'
                )}
                &nbsp;
                <strong>
                  {account.installerPartnerProgram.currentConnectorsCount ||
                    '-'}
                </strong>
              </p>
              <p>
                {t(
                  'settings.organization.installerPartnerProgram.remainingActiveConnectors',
                  'Remaining no. of active connectors to next tier:'
                )}
                &nbsp;
                <strong>{remainingConnectorsToNextTier || '-'}</strong>
              </p>
            </Stack>
            <Progress
              style={{ margin: 0 }}
              size="tiny"
              disabled={!progress}
              percent={progress || 100}
            />
            <Divider />
            <p>
              {t(
                'settings.organization.installerPartnerProgram.startDate',
                'Start date of the partner program:'
              )}
              &nbsp;
              <strong>
                {account.installerPartnerProgram?.startDate
                  ? formatDate(account.installerPartnerProgram?.startDate)
                  : '-'}
              </strong>
            </p>

            <Button
              style={{
                display: 'block',
                width: '100%',
                margin: 0,
              }}
              primary
              as={'a'}
              target="_blank"
              href={link}>
              {t(
                'settings.organization.installerPartnerProgram.learnMore',
                'Learn more about Partner Program Tiers'
              )}
            </Button>
          </Stack>
        </Segment>
      </>
    </SettingsShell>
  );
}
